import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLLMProviders,
  getLLMModelsWithInfo,
  getCurrentModelInfo,
  switchLLMModel,
  clearLLMModelCache,
  getFunctionConfigs,
  updateFunctionConfig,
  testModelConfig
} from '../services/api';
import {
  CpuChipIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  BeakerIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const ModelSwitcher = ({ showAsCard = true, className = '', showFunctionConfigs = false }) => {
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [editingFunction, setEditingFunction] = useState(null);
  const [functionModelEdit, setFunctionModelEdit] = useState({});

  // Fetch available providers
  const { data: providers, isLoading: providersLoading } = useQuery({
    queryKey: ['llmProviders'],
    queryFn: getLLMProviders,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch available models with metadata
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ['llmModelsWithInfo', selectedProvider],
    queryFn: () => getLLMModelsWithInfo(selectedProvider || null),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch current model info
  const { data: currentModel, isLoading: currentLoading } = useQuery({
    queryKey: ['currentModelInfo'],
    queryFn: getCurrentModelInfo,
    refetchInterval: 30000,
  });

  // Fetch function configurations
  const { data: functionConfigs } = useQuery({
    queryKey: ['functionConfigs'],
    queryFn: getFunctionConfigs,
    staleTime: 60 * 1000,
    enabled: showFunctionConfigs,
  });

  // Switch model mutation
  const switchModelMutation = useMutation({
    mutationFn: ({ provider, modelId }) => switchLLMModel(provider, modelId),
    onSuccess: () => {
      queryClient.invalidateQueries(['currentModelInfo']);
      setSelectedProvider('');
      setSelectedModel('');
    },
    onError: (error) => {
      console.error('Failed to switch model:', error);
    }
  });

  // Clear cache mutation
  const clearCacheMutation = useMutation({
    mutationFn: clearLLMModelCache,
    onSuccess: () => {
      queryClient.invalidateQueries(['currentModelInfo']);
    }
  });

  // Test model mutation
  const testModelMutation = useMutation({
    mutationFn: ({ modelId }) => testModelConfig(modelId),
    onSuccess: (data) => {
      setTestResult(data);
    },
    onError: (error) => {
      setTestResult({ success: false, error: error.message });
    }
  });

  // Update function config mutation
  const updateFunctionMutation = useMutation({
    mutationFn: ({ functionName, config }) => updateFunctionConfig(functionName, config),
    onSuccess: () => {
      queryClient.invalidateQueries(['functionConfigs']);
      setEditingFunction(null);
      setFunctionModelEdit({});
    },
    onError: (error) => {
      console.error('Failed to update function config:', error);
    }
  });

  // Set initial values when current model data loads
  useEffect(() => {
    if (currentModel) {
      setSelectedProvider(currentModel.provider || '');
      // Extract just the model ID without provider prefix for display
      const modelId = currentModel.model_id || '';
      setSelectedModel(modelId);
    }
  }, [currentModel]);

  const handleProviderChange = (provider) => {
    setSelectedProvider(provider);
    setSelectedModel('');
    setTestResult(null);
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    setTestResult(null);
  };

  const handleSwitchModel = async () => {
    if (!selectedProvider || !selectedModel) return;

    // Check if this is already the current model
    if (currentModel?.provider === selectedProvider &&
        currentModel?.model_id?.includes(selectedModel)) {
      return;
    }

    switchModelMutation.mutate({
      provider: selectedProvider,
      modelId: selectedModel
    });
  };

  const handleTestModel = () => {
    if (!selectedProvider || !selectedModel) return;
    const fullModelId = `${selectedProvider}/${selectedModel}`;
    testModelMutation.mutate({ modelId: fullModelId });
  };

  const handleClearCache = () => {
    clearCacheMutation.mutate();
  };

  const handleEditFunction = (funcName, config) => {
    setEditingFunction(funcName);
    setFunctionModelEdit({
      model_id: config.model_id,
      temperature: config.temperature,
      max_tokens: config.max_tokens,
      fallback_model_id: config.fallback_model_id || ''
    });
  };

  const handleSaveFunctionConfig = () => {
    if (!editingFunction) return;
    updateFunctionMutation.mutate({
      functionName: editingFunction,
      config: functionModelEdit
    });
  };

  const getModelDisplayName = (modelId) => {
    if (!modelId) return '';
    // Remove provider prefix if present
    const parts = modelId.split('/');
    const name = parts.length > 1 ? parts[parts.length - 1] : modelId;
    if (name.length > 35) {
      return name.substring(0, 32) + '...';
    }
    return name;
  };

  const getProviderModels = () => {
    if (!modelsData || !selectedProvider) return [];
    return modelsData[selectedProvider] || [];
  };

  const isCurrentModel = currentModel?.provider === selectedProvider &&
                         currentModel?.model_id?.includes(selectedModel);

  const isLoading = providersLoading || modelsLoading || currentLoading;

  if (isLoading) {
    return (
      <div className={`animate-pulse ${showAsCard ? 'bg-gray-50 p-4 rounded-lg' : ''} ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-8 bg-gray-200 rounded mb-2"></div>
        <div className="h-8 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const content = (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CpuChipIcon className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Model Selection</h3>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">LiteLLM</span>
        </div>
        <button
          onClick={handleClearCache}
          disabled={clearCacheMutation.isPending}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
          title="Clear model cache"
        >
          <TrashIcon className="h-4 w-4 mr-1" />
          Clear Cache
        </button>
      </div>

      {/* Current Model Status */}
      {currentModel && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Current: {currentModel.provider} / {getModelDisplayName(currentModel.model_id)}
              </p>
              <p className="text-xs text-blue-700">
                Cache: {currentModel.cache_size || 0} models | Functions: {currentModel.function_clients?.length || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Provider ({(Array.isArray(providers) ? providers.length : Object.keys(providers || {}).length) || 0} available)
        </label>
        <select
          value={selectedProvider}
          onChange={(e) => handleProviderChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        >
          <option value="">Select Provider</option>
          {(Array.isArray(providers) ? providers : Object.keys(providers || {})).map(provider => (
            <option key={provider} value={provider}>
              {provider.charAt(0).toUpperCase() + provider.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Model Selection with Metadata */}
      {selectedProvider && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model ({getProviderModels().length} available)
          </label>
          <select
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">Select Model</option>
            {getProviderModels().map(model => (
              <option key={model.model_id} value={model.model_id}>
                {model.display_name || model.model_id}
                {model.context_window && ` (${Math.round(model.context_window / 1000)}K ctx)`}
              </option>
            ))}
          </select>

          {/* Model Info Card */}
          {selectedModel && (
            <div className="mt-2 p-2 bg-gray-50 rounded-md text-xs">
              {(() => {
                const model = getProviderModels().find(m => m.model_id === selectedModel);
                if (!model) return null;
                return (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-gray-500">Max Tokens:</span>{' '}
                      <span className="font-medium">{model.max_tokens?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Context:</span>{' '}
                      <span className="font-medium">{model.context_window?.toLocaleString()}</span>
                    </div>
                    {model.input_cost_per_1k && (
                      <div>
                        <span className="text-gray-500">Input:</span>{' '}
                        <span className="font-medium">${model.input_cost_per_1k}/1K</span>
                      </div>
                    )}
                    {model.output_cost_per_1k && (
                      <div>
                        <span className="text-gray-500">Output:</span>{' '}
                        <span className="font-medium">${model.output_cost_per_1k}/1K</span>
                      </div>
                    )}
                    {model.supports_vision && (
                      <div className="col-span-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Vision Supported
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {selectedProvider && selectedModel && (
        <div className="flex space-x-2 pt-2">
          <button
            onClick={handleSwitchModel}
            disabled={switchModelMutation.isPending || isCurrentModel}
            className={`flex-1 flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isCurrentModel
                ? 'border-green-300 text-green-700 bg-green-50 cursor-not-allowed'
                : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 focus:ring-blue-500'
            } disabled:opacity-50`}
          >
            {switchModelMutation.isPending ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Switching...
              </>
            ) : isCurrentModel ? (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Current
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Switch
              </>
            )}
          </button>
          <button
            onClick={handleTestModel}
            disabled={testModelMutation.isPending}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {testModelMutation.isPending ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <BeakerIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      )}

      {/* Test Result */}
      {testResult && (
        <div className={`p-3 rounded-md ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-start">
            {testResult.success ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.success ? 'Test Passed' : 'Test Failed'}
              </p>
              {testResult.latency_ms && (
                <p className="text-xs text-green-600 mt-1">
                  Latency: {testResult.latency_ms.toFixed(0)}ms
                </p>
              )}
              {testResult.response_text && (
                <p className="text-xs text-gray-600 mt-1 truncate">
                  Response: {testResult.response_text.substring(0, 100)}...
                </p>
              )}
              {testResult.error && (
                <p className="text-xs text-red-600 mt-1">
                  {testResult.error}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {switchModelMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <p className="text-sm font-medium text-red-800">Failed to switch model</p>
              <p className="text-xs text-red-600 mt-1">
                {switchModelMutation.error?.response?.data?.detail || switchModelMutation.error?.message || 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Function-Specific Model Configuration */}
      {showFunctionConfigs && (
        <div className="border-t pt-4 mt-4">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center space-x-2">
              <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Per-Function Model Configuration</span>
            </div>
            {showAdvanced ? (
              <ChevronUpIcon className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {showAdvanced && functionConfigs && (
            <div className="mt-4 space-y-3">
              {Object.entries(functionConfigs).map(([funcName, config]) => (
                <div key={funcName} className="border rounded-md p-3 bg-gray-50">
                  {editingFunction === funcName ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 capitalize">{funcName.replace(/_/g, ' ')}</span>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Model ID</label>
                        <input
                          type="text"
                          value={functionModelEdit.model_id || ''}
                          onChange={(e) => setFunctionModelEdit({...functionModelEdit, model_id: e.target.value})}
                          className="w-full text-sm rounded border-gray-300"
                          placeholder="provider/model-name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Temperature</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="2"
                            value={functionModelEdit.temperature || 0.7}
                            onChange={(e) => setFunctionModelEdit({...functionModelEdit, temperature: parseFloat(e.target.value)})}
                            className="w-full text-sm rounded border-gray-300"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Max Tokens</label>
                          <input
                            type="number"
                            value={functionModelEdit.max_tokens || 4096}
                            onChange={(e) => setFunctionModelEdit({...functionModelEdit, max_tokens: parseInt(e.target.value)})}
                            className="w-full text-sm rounded border-gray-300"
                          />
                        </div>
                      </div>
                      <div className="flex space-x-2 mt-2">
                        <button
                          onClick={handleSaveFunctionConfig}
                          disabled={updateFunctionMutation.isPending}
                          className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {updateFunctionMutation.isPending ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {setEditingFunction(null); setFunctionModelEdit({});}}
                          className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer hover:bg-gray-100 -m-3 p-3 rounded-md transition-colors"
                      onClick={() => handleEditFunction(funcName, config)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 capitalize">{funcName.replace(/_/g, ' ')}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {config.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {config.model_id}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        temp: {config.temperature} | max: {config.max_tokens}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (showAsCard) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        {content}
      </div>
    );
  }

  return (
    <div className={className}>
      {content}
    </div>
  );
};

export default ModelSwitcher;
